import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/hooks/useAuth'
import { useMatch } from '@/shared/hooks/useMatches'
import { useMessages, useSendMessage, useMarkMessagesAsRead } from '@/shared/hooks/useMessages'
import { ChatMessage } from '@/shared/components/ChatMessage'
import { ChatInput } from '@/shared/components/ChatInput'
import { bridge } from '@/shared/bridge'
export function Chat() {
  const { matchId } = useParams<{ matchId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: match, isLoading: matchLoading } = useMatch(matchId!)
  const { data: messagesData, isLoading: messagesLoading } = useMessages(matchId!)
  const sendMessage = useSendMessage(matchId!)
  const markAsRead = useMarkMessagesAsRead(matchId!)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesData?.messages])

  useEffect(() => {
    if (!messagesData?.messages || !user?.id) return
    const unreadMessages = messagesData.messages.filter(
      (msg) => !msg.readAt && msg.senderId !== user.id,
    )
    if (unreadMessages.length > 0) {
      markAsRead.mutate(unreadMessages.map((msg) => msg.id))
    }
  }, [messagesData?.messages, user?.id])

  const handleSendMessage = async (content: string) => {
    try {
      await sendMessage.mutateAsync({
        type: 'TEXT',
        content,
      })
      bridge.haptic('light')
    } catch (err) {
      console.error('Failed to send message:', err)
      bridge.haptic('error')
    }
  }

  if (matchLoading || !match) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4 dark:bg-gray-950">
        <p className="text-gray-600 dark:text-gray-300">Загружаем чат...</p>
      </div>
    )
  }

  const displayName = match.name || match.profile?.name || 'Собеседник'
  const headerPhoto = match.profile?.photos?.[0]?.url || match.maxAvatarUrl || null

  return (
    <div className="flex h-screen flex-col bg-white dark:bg-gray-900">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/matches')}
              className="shrink-0 text-2xl text-gray-900 hover:opacity-80 dark:text-gray-100"
            >
              ←
            </button>
            {headerPhoto ? (
              <img
                src={headerPhoto}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-black/10"
              />
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-pink-400 text-sm font-bold text-white">
                {displayName[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-bold text-gray-900 dark:text-white">{displayName}</h1>
              {match.maxProfileUrl ? (
                <button
                  type="button"
                  onClick={() => bridge.openMaxLink(match.maxProfileUrl!)}
                  className="text-xs font-medium text-accent hover:underline"
                >
                  Профиль в MAX
                </button>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">Чат</p>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label="Меню (скоро)"
            className="shrink-0 text-xl text-gray-400 dark:text-gray-600"
            disabled
          >
            ⋮
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messagesLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`${
                    i % 2 === 0
                      ? 'rounded-3xl rounded-tr-md bg-accent/20'
                      : 'rounded-3xl rounded-tl-md bg-gray-200 dark:bg-gray-700'
                  } h-8 animate-pulse`}
                  style={{ width: `${Math.random() * 150 + 80}px` }}
                />
              </div>
            ))}
          </div>
        ) : messagesData?.messages && messagesData.messages.length > 0 ? (
          messagesData.messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              isOwn={message.senderId === user?.id}
              currentUserId={user?.id || ''}
            />
          ))
        ) : (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Начни общение! 💬</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput onSendMessage={handleSendMessage} isLoading={sendMessage.isPending} />
    </div>
  )
}
